import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { ProjectType } from "pages/OrganizationAdmin/types";
import { GroupType } from "pages/OrganizationAdmin/types";
import Group from "./Group";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  projectData: ProjectType;
  isLoaded: boolean;
  organizationCodename: string;
  refreshProject: () => void;
}

const Groups = memo(
  ({ projectData, organizationCodename, isLoaded, refreshProject }: Props) => {
    const [searchedGroups, setSearchedGroups] = useState<GroupType[]>([]);
    const [projectGroups, setProjectGroups] = useState<GroupType[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.groups
        .getGroups({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedGroups(data.results);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setProjectGroups(projectData.shared_to_groups);
    }, [projectData.shared_to_groups]);

    const addGroupToProject = (id: number) => {
      return api.resources.organization.projects
        .shareProjectWithGroup({
          organizationCodename: organizationCodename,
          id: projectData.id as number,
          userId: id,
        })
        .then(() => {
          refreshProject();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.group", "Group"),
              y: t("organization.project", "Project"),
            }),
          });
        });
    };

    const removeGroupFromProject = (id: number) => {
      return api.resources.organization.projects
        .unshareProjectWithGroup({
          organizationCodename: organizationCodename,
          id: projectData.id as number,
          userId: id,
        })
        .then(() => {
          refreshProject();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.group", "Group"),
              y: t("organization.project", "Project"),
            }),
          });
        });
    };

    const options = searchedGroups.map((groupData: GroupType) => ({
      id: groupData.id,
      label: groupData.name,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = projectGroups.findIndex((user) => {
          return user.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [projectGroups, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            y: t("organization.project", "Project"),
            x: t("organization.groups", "Groups"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="group"
          onChange={(_name, user) => {
            if (user) {
              addGroupToProject(user.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.groups", "Groups"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedTo", "{x} shared to {y}", {
            x: t("organization.project", "Project"),
            y: t("organization.groups", "Groups"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={projectData.shared_to_groups.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {projectGroups.map((group) => {
              return (
                <Group
                  name={group.name}
                  id={group.id}
                  key={group.id}
                  removeGroupFromProject={removeGroupFromProject}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Groups;
